import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  Checkbox,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Autocomplete,
  Drawer,
  Divider,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import "../../App.css";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import CustomPagination from "../../components/CustomPagination";
import FilterListIcon from "@mui/icons-material/FilterList";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import api from "../../api/client";
import { useQuery } from "@tanstack/react-query";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const SELECT_ALL_ID = "__select_all__";

const CheckboxMultiSelect = ({
  options,
  value,
  onChange,
  getId,
  getLabel,
  placeholder,
  noOptionsText,
}) => {
  const selectedOptions = options?.filter((item) => !item?.disabled)
  const allSelected = selectedOptions.length > 0 && value.length === selectedOptions.length;
  const someSelected = value.length > 0 && !allSelected;

  // The "Select All" row is a control, not real data, so it needs its own id and label.
  const idOf = (option) => (option === SELECT_ALL_ID ? SELECT_ALL_ID : getId(option));
  const labelOf = (option) => (option === SELECT_ALL_ID ? "Select All" : getLabel(option));

  const handleChange = (_event, newValue) => {
    const clickedSelectAll = newValue.some((option) => option === SELECT_ALL_ID);
    if (clickedSelectAll) {
      // Ticking it selects everything; ticking it again once everything is on clears the list.
      onChange(allSelected ? [] : selectedOptions);
      return;
    }
    onChange(newValue);
  };

  return (
    <Autocomplete
      size="small"
      multiple
      disableCloseOnSelect
      getOptionDisabled={(option) => option?.disabled}
      options={options.length ? [SELECT_ALL_ID, ...options] : []}
      value={value}
      onChange={handleChange}
      getOptionLabel={labelOf}
      isOptionEqualToValue={(option, selectedValue) => {
        const optionId = idOf(option);
        const selectedId = idOf(selectedValue);
        if (optionId == null || selectedId == null) return option === selectedValue;
        return optionId === selectedId;
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        const isSelectAllRow = option === SELECT_ALL_ID;
        return (
          <li {...optionProps} key={idOf(option)}>
            <Checkbox
              checked={isSelectAllRow ? allSelected : selected}
              indeterminate={isSelectAllRow && someSelected}
              sx={{ mr: 1 }}
            />
            {labelOf(option)}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={idOf(option)}
              label={labelOf(option)}
              size="small"
              sx={{ fontSize: "12px" }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : ""}
          fullWidth
          variant="outlined"
        />
      )}
      noOptionsText={noOptionsText}
    />
  );
};

const Setting = () => {
  // Get dynamic labels
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Role decides what the Map dialog does: a Super Admin shares a question bank with other
  // institutes, an Institute Admin assigns it to trainers. Each only fetches its own list.
  const role = localStorage.getItem("role");
  const isSuperAdmin = role === "SUPER_ADMIN";
  const isInstituteAdmin = role === "INSTITUTE_ADMIN";

  const [showTable, setShowTable] = useState(false);
  const [openDrawer, setOpen] = useState(false);
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [selectedQuestionBank, setSelectedQuestionBank] = useState(null);
  const [selectedInstitutes, setSelectedInstitutes] = useState([]);
  const location = useLocation();
  const { dashboard, viewAssessments } = location.state || "";

  // Get current user's institute_id
  const userData = JSON.parse(localStorage.getItem("userData")) || {};
  const currentUserInstituteId = userData.institute_id;

  const [appliedCategory, setAppliedCategory] = useState(null);
  const [appliedSubCategory, setAppliedSubCategory] = useState(null);
  const [appliedLevel, setAppliedLevel] = useState(null);
  const [appliedQuestionType, setAppliedQuestionType] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState(null);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState(["Beginner", "Intermediate", "Advanced"]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState(["MCQ", "Descriptive", "Fill in the blanks", "Circuit Lab"]);
  const [instituteOptions, setInstituteOptions] = useState([]);
  // Institute Admin assigns a question bank to trainers instead of to institutes.
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  // True while the assign request is in flight, so the dialog can't be submitted twice.
  const [assigning, setAssigning] = useState(false);

  // Institutes to share with — only a Super Admin picks these, so skip the request otherwise.
  const { data: institutesData, refetch: refetchInstistuteNames } = useQuery({
    queryKey: ["institutes"],
    queryFn: async () => {
      const response = await api.post("/ListAllInstituteIdNames", { gen_id: selectedQuestionBank.session_key });
      return response.data;
    },
    enabled: isSuperAdmin,
  });

  useEffect(() => {
    refetchInstistuteNames()
  }, [selectedQuestionBank])

  useEffect(() => {
    if (institutesData && institutesData.status === "success" && institutesData.data) {
      const instituteDatas = institutesData?.data?.map((item) => ({
        ...item,
        disabled: item?.is_assigned ? true : false
      }))
      setInstituteOptions(instituteDatas);
    }
  }, [institutesData]);

  // Trainers of the logged-in institute. Only an Institute Admin assigns to trainers, so
  // `enabled` keeps a Super Admin from making a request it would never use.
  const { data: trainersData } = useQuery({
    queryKey: ["instituteTrainers"],
    queryFn: async () => {
      const response = await api.get("/ListInstituteTrainers");
      return response.data;
    },
    enabled: isInstituteAdmin,
  });

  useEffect(() => {
    if (trainersData && trainersData.status === "success" && trainersData.data) {
      setTrainerOptions(trainersData.data);
    }
  }, [trainersData]);

  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState("");

  // Load search and filters from localStorage on mount
  useEffect(() => {
    const savedSearchKey = localStorage.getItem("questionBankSearchKey");
    const savedCategory = localStorage.getItem("questionBankCategory");
    const savedSubCategory = localStorage.getItem("questionBankSubCategory");
    const savedLevel = localStorage.getItem("questionBankLevel");
    const savedQuestionType = localStorage.getItem("questionBankQuestionType");
    const savedPage = localStorage.getItem("questionBankPage");

    if (savedSearchKey) setSearchKey(savedSearchKey);
    if (savedCategory) {
      const parsedCategory = JSON.parse(savedCategory);
      setAppliedCategory(parsedCategory);
      setSelectedCategory(parsedCategory);
    }
    if (savedSubCategory) {
      const parsedSubCategory = JSON.parse(savedSubCategory);
      setAppliedSubCategory(parsedSubCategory);
      setSelectedSubCategory(parsedSubCategory);
    }
    if (savedLevel) {
      setAppliedLevel(savedLevel);
      setSelectedLevel(savedLevel);
    }
    if (savedQuestionType) {
      setAppliedQuestionType(savedQuestionType);
      setSelectedQuestionType(savedQuestionType);
    }
    if (savedPage) setPage(parseInt(savedPage));
  }, []);

  // Save search and filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem("questionBankSearchKey", searchKey);
  }, [searchKey]);

  useEffect(() => {
    if (appliedCategory) {
      localStorage.setItem("questionBankCategory", JSON.stringify(appliedCategory));
    } else {
      localStorage.removeItem("questionBankCategory");
    }
  }, [appliedCategory]);

  useEffect(() => {
    if (appliedSubCategory) {
      localStorage.setItem("questionBankSubCategory", JSON.stringify(appliedSubCategory));
    } else {
      localStorage.removeItem("questionBankSubCategory");
    }
  }, [appliedSubCategory]);

  useEffect(() => {
    if (appliedLevel) {
      localStorage.setItem("questionBankLevel", appliedLevel);
    } else {
      localStorage.removeItem("questionBankLevel");
    }
  }, [appliedLevel]);

  useEffect(() => {
    if (appliedQuestionType) {
      localStorage.setItem("questionBankQuestionType", appliedQuestionType);
    } else {
      localStorage.removeItem("questionBankQuestionType");
    }
  }, [appliedQuestionType]);

  useEffect(() => {
    localStorage.setItem("questionBankPage", page);
  }, [page]);

  const headings = [
    { label: "S.No", align: "center" },
    { label: `${getLabel("PROGRAM_CODE")}/${getLabel("PROGRAMS")}`, align: "left" },
    { label: `${getLabel("SUBJECT_CODE")}/${getLabel("SUBJECT")}`, align: "left" },
    { label: `${getLabel("INSTITUTE")} Name`, align: "left" },
    { label: "Subtopic", align: "left" },
    { label: "Question Type/Level", align: "left" },
    { label: "Actions", align: "center" },
  ];

  const fetchQuestionBankList = async () => {
    const payload = {
      page: page,
      size: rowsPerPage,
      search_key: searchKey || "",
      category_id: appliedCategory?.category_id || null,
      subcategory_id: appliedSubCategory?.subcategory_id || null,
      level: appliedLevel || "",
      question_type: appliedQuestionType || "",
    };

    const res = await api.post("QuestionBankSessionsList", payload);

    if (res?.data?.status === "success") {
      return {
        data: res.data.data || [],
        total: res.data.total || res.data.data?.length || 0,
      };
    } else {
      return {
        data: [],
        total: 0,
      };
    }
  };

  const { data: queryData, isLoading, refetch } = useQuery({
    queryKey: ["questionBankList", page, rowsPerPage, searchKey, appliedCategory, appliedSubCategory, appliedLevel, appliedQuestionType],
    queryFn: fetchQuestionBankList,
  });

  const listData = queryData?.data || [];
  const count = queryData?.total || 0;

  // Refetch list when component mounts or location changes (e.g., navigating back from AddCategory)
  useEffect(() => {
    refetch();
  }, [location.pathname]);

  // Fetch dropdown options from APIs
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        // Fetch Categories
        const categoriesRes = await api.post("GetBankCategories");
        if (categoriesRes?.data?.status === "success") {
          setCategoryOptions(categoriesRes.data.categories || []);
        }
      } catch (error) {
        console.log("Error fetching dropdown options:", error);
      }
    };

    fetchDropdownOptions();
  }, []);

  // Fetch SubCategories based on selected category
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const categoryId = selectedCategory?.category_id || 0;
        const subCategoriesRes = await api.post("GetBankSubCategories", {
          category_id: categoryId
        });
        if (subCategoriesRes?.data?.status === "success") {
          setSubCategoryOptions(subCategoriesRes.data.subcategories || []);
        }
      } catch (error) {
        console.log("Error fetching subcategories:", error);
      }
    };

    if (selectedCategory) {
      fetchSubCategories();
    } else {
      setSubCategoryOptions([]);
    }
  }, [selectedCategory]);

  const handlechangeSearch = (e) => {
    setPage(0);
    setSearchKey(e.target.value);
  };

  const handleAddCategory = () => {
    navigate("/addCategory", {
      state: { newCategory: true }
    });
  };

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedCategory(selectedCategory || null);
    setAppliedSubCategory(selectedSubCategory || null);
    setAppliedLevel(selectedLevel);
    setAppliedQuestionType(selectedQuestionType);
    setOpen(false);
    refetch();
  };

  const handleEdit = (id) => {
    navigate("/addCategory", {
      state: {
        assessment_id: id,
        session_key: id,
        edit: true,
      },
    });
  };

  const handleView = (item) => {
    navigate("/questionBank/view", {
      state: {
        assessment_id: item.session_key,
        category_id: item.category_id,
        subcategory_id: item.subcategory_id,
      },
    });
  };

  const handleClickClear = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedLevel(null);
    setSelectedQuestionType(null);

    setAppliedCategory(null);
    setAppliedSubCategory(null);
    setAppliedLevel(null);
    setAppliedQuestionType(null);

    setSearchKey("");
    setPage(0);
    refetch();

    // Clear localStorage
    localStorage.removeItem("questionBankSearchKey");
    localStorage.removeItem("questionBankCategory");
    localStorage.removeItem("questionBankSubCategory");
    localStorage.removeItem("questionBankLevel");
    localStorage.removeItem("questionBankQuestionType");
    localStorage.removeItem("questionBankPage");
  };

  const canSeeMap = isSuperAdmin || isInstituteAdmin;
  // A Super Admin may only share a bank whose owning institute has opted in via the
  // "Can we share this institute's question banks to all institutes?" switch, which the
  // list returns per row as can_share_question_banks. An Institute Admin is unrestricted.
  const canMapItem = (item) => (isSuperAdmin ? !!item.is_root_institute : true);

  const trainerId = (trainer) => trainer?.trainer_user_id;
  // Names can carry double spaces ("Ram  M"), so collapse them before displaying.
  const trainerLabel = (trainer) => {
    const name = (trainer?.name || "").replace(/\s+/g, " ").trim();
    return name || trainer?.email || `${getLabel('TRAINER')} ${trainerId(trainer) ?? ""}`.trim();
  };

  const handleMap = (item) => {
    setSelectedQuestionBank(item);
    setSelectedInstitutes([]);
    setSelectedTrainers([]);
    setOpenMapDialog(true);
  };

  // Institute Admin: give the picked trainers access to this question bank.
  const assignToTrainers = async () => {
    if (selectedTrainers.length === 0) {
      showSnackbar("error", "Please select at least one trainer");
      return;
    }

    setAssigning(true);
    try {
      const payload = {
        gen_id: selectedQuestionBank.session_key,
        trainer_user_ids: selectedTrainers.map(trainerId),
      };

      const response = await api.post("/AssignQuestionBanksToTrainer", payload);

      if (response.data.status === "success") {
        showSnackbar("success", "Assigned successfully");
        handleCloseMapDialog();
      } else {
        showSnackbar("error", response.data.message || "Failed to assign trainer");
      }
    } catch (error) {
      console.error("Error assigning trainer:", error);
      showSnackbar("error", error?.response?.data?.message || "Failed to assign trainer");
    } finally {
      setAssigning(false);
    }
  };

  // Super Admin: share this question bank with the picked institutes.
  const assignToInstitutes = async () => {
    if (selectedInstitutes.length === 0) {
      showSnackbar("error", "Please select at least one institute");
      return;
    }

    setAssigning(true);
    try {
      const payload = {
        gen_id: selectedQuestionBank.session_key,
        institute_ids: selectedInstitutes.map((institute) => institute.institute_id),
      };

      const response = await api.post("/ShareQuestionBankAccess", payload);

      if (response.data.status === "success") {
        showSnackbar("success", "Assigned successfully");
        handleCloseMapDialog();
      } else {
        showSnackbar("error", response.data.message || "Failed to assign institute");
      }
    } catch (error) {
      console.error("Error assigning institute:", error);
      showSnackbar("error", error?.response?.data?.message || "Failed to assign institute");
    } finally {
      setAssigning(false);
    }
  };

  // Whichever list the current role uses, the Assign button runs its own action.
  const handleAssign = () => (isInstituteAdmin ? assignToTrainers() : assignToInstitutes());

  const handleCloseMapDialog = () => {
    setOpenMapDialog(false);
    setSelectedQuestionBank(null);
    setSelectedInstitutes([]);
    setSelectedTrainers([]);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {`${getLabel('SUBJECT')}`}
          </Typography>
          <Autocomplete
            size="small"
            options={categoryOptions}
            value={selectedCategory}
            onChange={(event, newValue) => setSelectedCategory(newValue)}
            getOptionLabel={(option) => option.category_name || ""}
            isOptionEqualToValue={(option, value) => option?.category_id === value?.category_id}
            renderInput={(params) => (
              <TextField {...params} placeholder="All Subjects" fullWidth variant="outlined" />
            )}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Subtopic
          </Typography>
          <Autocomplete
            size="small"
            options={subCategoryOptions}
            value={selectedSubCategory}
            onChange={(event, newValue) => setSelectedSubCategory(newValue)}
            getOptionLabel={(option) => option.subcategory_name || option.sub_category_name || ""}
            isOptionEqualToValue={(option, value) => option?.subcategory_id === value?.subcategory_id}
            renderInput={(params) => (
              <TextField {...params} placeholder="All Subtopics" fullWidth variant="outlined" />
            )}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Level
          </Typography>
          <Autocomplete
            size="small"
            options={levelOptions}
            value={selectedLevel}
            onChange={(event, newValue) => setSelectedLevel(newValue)}
            renderInput={(params) => (
              <TextField {...params} placeholder="All Levels" fullWidth variant="outlined" />
            )}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Question Type
          </Typography>
          <Autocomplete
            size="small"
            options={questionTypeOptions}
            value={selectedQuestionType}
            onChange={(event, newValue) => setSelectedQuestionType(newValue)}
            renderInput={(params) => (
              <TextField {...params} placeholder="All Question Types" fullWidth variant="outlined" />
            )}
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="customButton"
          onClick={handleApplyFilters}
          sx={{ mt: 2 }}
        >
          Apply Filters
        </Button>
      </Drawer>

      {(dashboard === true || viewAssessments === true) && (
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "fit-content",
            mb: 2,
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.history.back();
            }
          }}
          onClick={() => window.history.back()}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}>
            Back
          </Typography>
        </Grid>
      )}

      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            mb: 2,
          }}
        >
          <Grid>
            <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <Box className="icon-box">
                <DescriptionOutlinedIcon className="iconHeading" />
              </Box>
              <Box sx={{ ml: 2 }}>
                <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
                  {getLabel("QUESTION_BANK")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid sx={{ display: "flex", gap: 4 }}>

            <TextField
              value={searchKey}
              size="small"
              variant="outlined"
              placeholder={`Search ${getLabel("SUBJECT")}`}
              onChange={(e) => handlechangeSearch(e)}
              className="custom-search-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {localStorage.getItem("role") !== "SUPER_ADMIN" && (
              <Button
                variant="contained"
                className="gradient-button"
                onClick={handleAddCategory}
                startIcon={<AddIcon sx={{ color: "white" }} />}
              >
                <Typography sx={{ color: "white" }}>Create New {getLabel("BANK")}</Typography>
              </Button>
            )}
          </Grid>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            opacity: showTable ? 1 : 0,
            transform: showTable ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={headings.length}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {appliedCategory && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{appliedCategory.category_name}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedCategory(null);
                              setSelectedCategory(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                      {appliedSubCategory && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{appliedSubCategory.subcategory_name || appliedSubCategory.sub_category_name}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedSubCategory(null);
                              setSelectedSubCategory(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                      {appliedLevel && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{appliedLevel}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedLevel(null);
                              setSelectedLevel(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                      {appliedQuestionType && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{appliedQuestionType}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedQuestionType(null);
                              setSelectedQuestionType(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton onClick={() => setOpen(true)} color="primary" className="more-filter-btn">
                        <FilterListIcon />
                        <Typography className="more-filter-text">More filter</Typography>
                      </IconButton>

                      {(appliedCategory || appliedSubCategory || appliedLevel || appliedQuestionType) && (
                        <Button variant="outlined" className="clear-filter-btn" onClick={handleClickClear}>
                          Clear Filter
                        </Button>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                {headings.map((heading, index) => (
                  <TableCell key={index} align={heading.align} className="tableHeader">
                    <b>{heading.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody className="tablebody">
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={headings.length} align="center" sx={{ py: 4 }}>
                    <TableLoader message="Fetching question banks..." />
                  </TableCell>
                </TableRow>
              ) : listData?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headings.length} align="center" sx={{ py: 10 }}>
                    <TableNodata />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {listData.map((item, index) => (
                    <TableRow
                      key={item.session_key}
                      hover
                      sx={{
                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                    >
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>

                      {/* Program: code + name */}
                      <TableCell sx={{ maxWidth: '200px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                          {item.program_code && (
                            <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#1c97e3', letterSpacing: '0.3px' }}>
                              {item.program_code || "-"}
                            </Typography>
                          )}
                          <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a', wordBreak: 'break-word', lineHeight: 1.3 }}>
                            {item.program_name || "-"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Subject: category + code */}
                      <TableCell sx={{ maxWidth: '220px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                          {item.category_code && (
                            <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#1c97e3', letterSpacing: '0.3px' }}>
                              {item.category_code || "-"}
                            </Typography>
                          )}
                          <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a', wordBreak: 'break-word', lineHeight: 1.3 }}>
                            {item.category || "-"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Institute Name */}
                      <TableCell sx={{ color: "#546e7a" }}>
                        {item.institute_name || '-'}
                      </TableCell>

                      {/* Sub category */}
                      <TableCell sx={{ color: "#546e7a" }}>
                        {item.subcategory || '-'}
                      </TableCell>

                      {/* Question type */}
                      <TableCell sx={{ minWidth: '140px' }}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.6,
                          p: 0.8,
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <Chip
                            label={item.lab_tool_name ? `${item.question_type?.toUpperCase()} - ${item.lab_tool_name}` : item.question_type?.toUpperCase() || "N/A"}
                            size="small"
                            color={
                              item.question_type?.toUpperCase() === "MCQ" || item.question_type?.toUpperCase() === "MULTIPLE CHOICE"
                                ? "secondary"
                                : "primary"
                            }
                            variant="filled"
                            sx={{
                              fontWeight: 600,
                              fontSize: '11px',
                              color: "white",
                              height: '24px',
                              borderRadius: '4px',
                              justifyContent: 'center',
                            }}
                          />
                          <Chip
                            label={item.level || "N/A"}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              fontSize: '11px',
                              height: '22px',
                              borderRadius: '4px',
                              color: '#64748b',
                              borderColor: '#cbd5e1',
                              backgroundColor: 'white',
                              justifyContent: 'center',
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px 10px",
                          whiteSpace: "nowrap",
                          width: "1%",
                        }}
                      >
                        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                          <IconButton
                            size="small"
                            onClick={() => handleView(item)}
                            sx={{ display: "flex", gap: 0.5, p: 0 }}
                          >
                            <VisibilityIcon color="primary" />
                            <Typography color="primary" sx={{ fontSize: "12px" }}>
                              View
                            </Typography>
                          </IconButton>

                          {canSeeMap && (
                            canMapItem(item) ? (
                              <IconButton
                                size="small"
                                onClick={() => handleMap(item)}
                                sx={{ display: "flex", gap: 0.5, p: 0 }}
                              >
                                <LinkIcon color="primary" />
                                <Typography color="primary" sx={{ fontSize: "12px" }}>
                                  Map
                                </Typography>
                              </IconButton>
                            ) : (
                              <Tooltip
                                title={`${item.institute_name || "This institute"} has not allowed its question banks to be shared`}
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled
                                    sx={{ display: "flex", gap: 0.5, p: 0 }}
                                  >
                                    <LinkIcon color="action" />
                                    <Typography color="textSecondary" sx={{ fontSize: "12px" }}>
                                      Map
                                    </Typography>
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )
                          )}

                          {/* {localStorage.getItem("role") !== "SUPER_ADMIN" && (
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item.session_key)}
                              sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 0 }}
                            >
                              <EditIcon color="primary" />
                              <Typography color="primary" sx={{ fontSize: "12px" }}>
                                Edit
                              </Typography>
                            </IconButton>
                          )} */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}

                  {((page === 0 && listData?.length > rowsPerPage - 1) || page !== 0) && (
                    <CustomPagination
                      page={page}
                      setPage={setPage}
                      count={count}
                      rowsPerPage={rowsPerPage}
                      colSpan={headings.length}
                    />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>

      {/* Map Institute Dialog */}
      <Dialog
        open={openMapDialog}
        // Passing no handler while the request runs stops Esc / backdrop clicks from
        // closing the dialog mid-assign and clearing the bank it is posting about.
        onClose={assigning ? undefined : handleCloseMapDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: "500px",
            borderRadius: "12px",
            p: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <LinkIcon
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              fontSize: "30px",
              borderRadius: "50%",
              padding: "5px",
            }}
          />
          <DialogTitle sx={{ fontWeight: 700, color: "#1976d2", padding: "5px" }}>
            {isInstituteAdmin ? `Assign ${getLabel("TRAINER")} to ${getLabel("QUESTION_BANK")}` : `Map ${getLabel("INSTITUTE")} to ${getLabel("QUESTION_BANK")}`}
          </DialogTitle>
        </Box>
        <Divider sx={{ mt: 2, borderColor: "black", borderBottomWidth: 1, mx: 0 }} />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <Box sx={{ backgroundColor: "#f8fafc", p: 2, borderRadius: "8px", border: "1px solid #e2e8f0", mb: 2 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#64748b", mb: 0.5 }}>
                {`${getLabel("QUESTION_BANK")}`}:
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a" }}>
                {selectedQuestionBank?.category || "N/A"}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#546e7a", mt: 0.5 }}>
                {selectedQuestionBank?.program_name || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#1a1a1a" }}>
                {isInstituteAdmin ? `Select ${getLabel("TRAINER")}` : `Select ${getLabel("INSTITUTE")}`}{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>

            {/* An Institute Admin hands the bank to trainers; a Super Admin shares it
                with other institutes. Same dropdown, different list. */}
            {isInstituteAdmin ? (
              <CheckboxMultiSelect
                options={trainerOptions}
                value={selectedTrainers}
                onChange={setSelectedTrainers}
                getId={trainerId}
                getLabel={trainerLabel}
                placeholder={`Select ${getLabel("TRAINER")}`}
                noOptionsText="No trainers found"
              />
            ) : (
              <CheckboxMultiSelect
                options={instituteOptions}
                value={selectedInstitutes}
                onChange={setSelectedInstitutes}
                getId={(institute) => institute.institute_id}
                getLabel={(institute) => institute.name || ""}
                placeholder={`Select ${getLabel("INSTITUTE")}`}
                noOptionsText="No institutes found"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseMapDialog}
            variant="outlined"
            disabled={assigning}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            color="primary"
            disabled={
              assigning ||
              (isInstituteAdmin ? selectedTrainers.length === 0 : selectedInstitutes.length === 0)
            }
            startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {assigning ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Setting;
